/**
 * PATH: packages/ui/src/qa/QaNoteThread.tsx
 * FILE_ID: FE-QA-NOTE-THREAD-001
 * PURPOSE: Attributed, timestamped note list plus composer for QA execution,
 *          shared by both QA front doors.
 * WHY: apps/qa and packages/job-hub share no package except @quotech/ui, so a
 *      presentational leaf here is the only way to keep the two doors at parity
 *      instead of drifting into two note UIs. Deliberately API-free: each door
 *      owns its client and passes a mapped list plus the handlers it actually
 *      supports. `onEdit`/`onDelete` are optional because the QA hub already
 *      allowed editing and deleting its single item note and must not lose
 *      that, while the Jobs Hub client has no note-update endpoint.
 * FLOW: caller maps Evidence(type='note') -> QaNote[] -> thread renders them in
 *       the order supplied -> composer calls onAdd -> caller writes the row and
 *       refetches. A rejected onAdd keeps the draft on screen.
 */

/* eslint-disable react-refresh/only-export-components -- stamp helpers live with the thread */
import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface QaNote {
  id: string
  text: string
  /** `captured_by_name`; may be blank when the user has no full name set. */
  authorName?: string | null
  /** ISO timestamp, i.e. `captured_at`. */
  at: string
}

export interface QaNoteThreadProps {
  /** Rendered in the order given; the caller decides newest- or oldest-first. */
  notes: QaNote[]
  onAdd: (text: string) => void | Promise<void>
  /** Omit to render notes as immutable. */
  onEdit?: (noteId: string, text: string) => void | Promise<void>
  /** Omit to render notes as undeletable. */
  onDelete?: (noteId: string) => void | Promise<void>
  /** Read-only mode, e.g. a locked area. Hides every control, keeps the list. */
  disabled?: boolean
  busy?: boolean
  placeholder?: string
  emptyLabel?: string
  className?: string
  /** Tenant-neutral localStorage suffix: `qa.note.draft.<draftKey>`. */
  draftKey?: string
  /** Submit a non-empty draft on blur or unmount. Default off. */
  commitOnUnmount?: boolean
  /**
   * Render the composer. Default true. When false only the note list shows —
   * existing notes are evidence and must never hide behind the composer
   * toggle. Flipping true -> false submits a non-empty draft once when
   * `commitOnUnmount` is set, so collapsing the composer never loses text.
   */
  showComposer?: boolean
}

const UNATTRIBUTED = 'Unattributed'

function draftStorageKey(draftKey?: string): string | null {
  return draftKey ? `qa.note.draft.${draftKey}` : null
}

function readDraft(draftKey?: string): string {
  const key = draftStorageKey(draftKey)
  if (!key || typeof window === 'undefined') return ''
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function writeDraft(draftKey: string | undefined, value: string): void {
  const key = draftStorageKey(draftKey)
  if (!key || typeof window === 'undefined') return
  try {
    if (value) window.localStorage.setItem(key, value)
    else window.localStorage.removeItem(key)
  } catch {
    /* quota / private mode */
  }
}

/** Date + time, because a QA note is evidence of WHEN something was observed. */
export function formatNoteStamp(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function noteAuthorLabel(authorName?: string | null): string {
  const trimmed = (authorName ?? '').trim()
  return trimmed || UNATTRIBUTED
}

function NoteRow({
  note,
  onEdit,
  onDelete,
  disabled,
}: {
  note: QaNote
  onEdit?: (noteId: string, text: string) => void | Promise<void>
  onDelete?: (noteId: string) => void | Promise<void>
  disabled: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note.text)

  const canEdit = Boolean(onEdit) && !disabled
  const canDelete = Boolean(onDelete) && !disabled

  if (editing && onEdit) {
    return (
      <li className="rounded-lg border border-primary/40 bg-background px-3 py-2">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={2}
          aria-label="Edit note"
          className="w-full resize-none rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-primary"
        />
        <div className="mt-1.5 flex gap-2">
          <button
            type="button"
            onClick={() => {
              const text = draft.trim()
              if (text && text !== note.text) void onEdit(note.id, text)
              setEditing(false)
            }}
            className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(note.text)
              setEditing(false)
            }}
            className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground"
          >
            Cancel
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className="rounded-lg border border-border bg-muted/40 px-3 py-2">
      <p className="whitespace-pre-wrap break-words text-sm text-foreground">{note.text}</p>
      <div className="mt-1 flex items-center justify-between gap-2">
        <p className="text-[10px] text-muted-foreground">
          {noteAuthorLabel(note.authorName)} · {formatNoteStamp(note.at)}
        </p>
        {(canEdit || canDelete) && (
          <div className="flex shrink-0 gap-2">
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-[10px] text-muted-foreground underline"
                aria-label="Edit note"
              >
                Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => void onDelete?.(note.id)}
                className="rounded-full border border-border bg-background p-0.5"
                aria-label="Delete note"
                title="Delete note"
              >
                <X className="h-3 w-3 text-destructive" aria-hidden />
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

export function QaNoteThread({
  notes,
  onAdd,
  onEdit,
  onDelete,
  disabled = false,
  busy = false,
  placeholder = 'Add a note...',
  // Callers may still pass emptyLabel; an empty list shows nothing.
  emptyLabel: _emptyLabel = 'No notes yet.',
  className,
  draftKey,
  commitOnUnmount = false,
  showComposer = true,
}: QaNoteThreadProps) {
  const [draft, setDraft] = useState(() => readDraft(draftKey))
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const draftRef = useRef(draft)
  const inFlightRef = useRef(false)
  const onAddRef = useRef(onAdd)
  const busyRef = useRef(busy)
  draftRef.current = draft
  onAddRef.current = onAdd
  busyRef.current = busy

  const pending = saving || busy

  const submit = useCallback(async () => {
    const text = draftRef.current.trim()
    if (!text || busyRef.current || inFlightRef.current) return
    inFlightRef.current = true
    setSaving(true)
    setFailed(false)
    try {
      await onAddRef.current(text)
      draftRef.current = ''
      setDraft('')
      writeDraft(draftKey, '')
    } catch {
      // Keep the draft: losing a note typed on site is the failure this
      // surface exists to prevent.
      setFailed(true)
    } finally {
      inFlightRef.current = false
      setSaving(false)
    }
  }, [draftKey])

  useEffect(() => {
    if (!commitOnUnmount) return undefined
    return () => {
      void submit()
    }
  }, [commitOnUnmount, submit])

  // Collapsing the composer (showComposer true -> false) commits like an
  // unmount would: the thread itself stays mounted to keep the notes visible.
  const prevShowComposerRef = useRef(showComposer)
  useEffect(() => {
    if (prevShowComposerRef.current && !showComposer && commitOnUnmount) {
      void submit()
    }
    prevShowComposerRef.current = showComposer
  }, [showComposer, commitOnUnmount, submit])

  return (
    <div className={['space-y-2', className].filter(Boolean).join(' ')}>
      {notes.length > 0 ? (
        <ul className="space-y-1.5">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              onEdit={onEdit}
              onDelete={onDelete}
              disabled={disabled}
            />
          ))}
        </ul>
      ) : null}

      {!disabled && showComposer && (
        <div className="space-y-1.5">
          <textarea
            value={draft}
            onChange={(event) => {
              const next = event.target.value
              setDraft(next)
              writeDraft(draftKey, next)
            }}
            onBlur={() => {
              if (commitOnUnmount) void submit()
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                void submit()
              }
            }}
            placeholder={placeholder}
            rows={2}
            aria-label="Note"
            className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
          />
          {failed && (
            <p className="text-xs text-red-600 dark:text-red-400">
              Could not save that note. Your text is still here, try again.
            </p>
          )}
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!draft.trim() || pending}
            className="min-h-9 w-full rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {pending ? 'Saving...' : 'Add note'}
          </button>
        </div>
      )}
    </div>
  )
}
